import { useState, useCallback, useRef, useEffect } from 'react';
import { SprintAPI } from '../utils/api';
import { logAuditEvent, saveSprintSnapshot } from '../utils/auditLog';
import { shouldAllowEmptyTasksSave, clearIntentionalEmptyTasksSave } from '../utils/allowEmptyTasksSave';
import { APP_CONFIG } from '../config/app';
import { getActiveWorkspaceId, PERSONAL_WORKSPACE_ID } from '../utils/api/workspaceContext';
import { useAuth } from '../components/AuthProvider';
import { clearPendingTaskAdds, queuePendingTaskAdds } from '../utils/pendingSprintTaskAdds';
import { isDynamoItemSizeError, maybeWarnSprintItemSize } from '../utils/sprintItemSize';
import { isSprintSaveHeld } from '../utils/sprintSaveHold';
import { showToast } from '../components/Toast';

// Minimum time between API saves (throttle)
const MIN_SAVE_INTERVAL_MS = 2000; // 2 seconds

/**
 * useSaveManagement Hook
 * 
 * Saves changes to DynamoDB with throttling to prevent excessive API calls.
 * 
 * THROTTLING:
 * - Minimum 2 second interval between saves
 * - If a save is requested within the interval, it's queued and executed after the interval
 * - This prevents "a zillion" saves during continuous operations like resize/drag
 * 
 * SAFEGUARDS:
 * - Tracks last saved task count per sprint
 * - Blocks saves that would delete all tasks
 * - Comprehensive logging
 */
export function useSaveManagement() {
  const { workspaceId: authWorkspaceId } = useAuth();
  const workspacePinRef = useRef(authWorkspaceId);
  workspacePinRef.current = authWorkspaceId;

  const pinWorkspace = useCallback(() => {
    const fromAuth = workspacePinRef.current;
    if (fromAuth && fromAuth !== PERSONAL_WORKSPACE_ID) return fromAuth;
    return getActiveWorkspaceId();
  }, []);

  // Save status indicator: 'idle' | 'saving' | 'saved'
  const [saveStatus, setSaveStatus] = useState('idle');
  const saveStatusTimeoutRef = useRef(null);
  
  // Track last saved task count per sprint (safeguard against data loss)
  const lastSavedTaskCountRef = useRef({});
  // Track maximum task count ever seen per sprint (stronger safeguard)
  const maxTaskCountRef = useRef({});
  
  // Throttling state
  const lastSaveTimeRef = useRef({}); // Per-sprint last save timestamp
  const pendingSaveRef = useRef({}); // Per-sprint { sprint, workspaceId } pending save data
  const pendingSaveTimerRef = useRef({}); // Per-sprint pending save timer
  const latestPayloadRef = useRef({}); // Per-sprint latest body waiting for the save chain
  const saveChainRef = useRef({}); // Per-sprint Promise chain — never let an older PUT finish last
  const abortRef = useRef({}); // Per-sprint AbortController for the in-flight PUT
  const saveGenRef = useRef({});

  // Internal save function (actually performs the API call).
  // `workspaceId` is the workspace that was active when the save was REQUESTED,
  // not when the request fires — throttled saves and in-flight requests must
  // never follow the user into a different workspace (cross-workspace bleed).
  const performSave = useCallback(async (sprint, workspaceId) => {
    if (!sprint) return;
    if (isSprintSaveHeld(sprint.id)) return;
    
    // CRITICAL: Ensure required arrays exist (API validation)
    // If these are missing, the sprint is likely metadata-only and shouldn't be saved
    if (!Array.isArray(sprint.tasks)) {
      // Sprint is metadata-only - skip save silently (this is expected during lazy loading)
      return;
    }
    
    // Ensure categories exists (default to empty array if missing)
    const sprintToSave = {
      ...sprint,
      categories: sprint.categories || [],
    };
    
    const taskCount = sprintToSave.tasks?.length || 0;
    const lastSavedCount = lastSavedTaskCountRef.current[sprintToSave.id] || 0;
    const maxSeenCount = maxTaskCountRef.current[sprintToSave.id] || 0;
    
    // Update max task count tracker (before any blocking checks)
    if (taskCount > 0) {
      maxTaskCountRef.current[sprintToSave.id] = Math.max(maxSeenCount, taskCount);
    }
    
    // CRITICAL SAFEGUARD: Block saves that would delete all tasks
    // Uses BOTH lastSavedCount AND maxSeenCount to catch the case where
    // a sprint is loaded with 0 tasks due to a bug (lastSavedCount would be 0)
    const referenceCount = Math.max(lastSavedCount, maxSeenCount);
    const allowEmptySave = taskCount === 0 && shouldAllowEmptyTasksSave(sprintToSave.id);

    if (taskCount === 0 && referenceCount > 0 && !allowEmptySave) {
      logAuditEvent('SAVE_BLOCKED_ALL_TASKS_DELETED', {
        sprintId: sprintToSave.id,
        sprintName: sprintToSave.name,
        lastSavedCount,
        maxSeenCount,
        currentCount: taskCount,
        reason: 'Blocked save that would delete all tasks - likely a bug',
      });
      console.error(
        `🛑 BLOCKED SAVE: Attempted to save "${sprintToSave.name}" with 0 tasks ` +
        `(lastSaved: ${lastSavedCount}, maxSeen: ${maxSeenCount}). ` +
        `This is likely a bug. Check the audit log for details.`
      );
      return; // DON'T SAVE - this would wipe all data
    }
    
    // LOG significant task count decreases for audit trail (but don't block).
    // Blocking >50% deletions was removed because it silently prevented legitimate
    // bulk-delete operations from persisting. The zero-task safeguard above is
    // sufficient to catch catastrophic bugs.
    if (taskCount > 0 && referenceCount > 5 && taskCount < referenceCount * 0.5) {
      logAuditEvent('SIGNIFICANT_TASK_DECREASE', {
        sprintId: sprintToSave.id,
        sprintName: sprintToSave.name,
        lastSavedCount,
        maxSeenCount,
        currentCount: taskCount,
        percentLost: Math.round((1 - taskCount / referenceCount) * 100),
      });
    }
    
    maybeWarnSprintItemSize(sprintToSave);

    // Save snapshot before API call (for recovery if something goes wrong)
    saveSprintSnapshot(sprintToSave);
    
    logAuditEvent('SAVE_TO_API', {
      sprintId: sprintToSave.id,
      sprintName: sprintToSave.name,
      taskCount,
      lastSavedCount,
      hasViews: !!sprintToSave.views,
      viewsCount: sprintToSave.views?.length || 0,
      hasDefaultViewSettings: !!sprintToSave.defaultViewSettings,
    });
    
    setSaveStatus('saving');
    abortRef.current[sprintToSave.id]?.abort();
    const ac = new AbortController();
    abortRef.current[sprintToSave.id] = ac;
    const gen = (saveGenRef.current[sprintToSave.id] || 0) + 1;
    saveGenRef.current[sprintToSave.id] = gen;

    try {
      try {
        await SprintAPI.update(sprintToSave.id, sprintToSave, {
          workspaceId,
          signal: ac.signal,
        });
      } catch (updateError) {
        if (updateError.name === 'AbortError' || /aborted/i.test(updateError.message || '')) return;
        // Nested campaigns are minted client-side (nested-sprint-*); if create
        // never landed, the first update 404s — create instead.
        // ONLY for that id shape: a blind create-on-404 would resurrect deleted
        // campaigns, or (before workspace pinning) recreate a campaign inside
        // whichever workspace happened to be active when the request fired.
        const notFound = /not found/i.test(updateError.message || '');
        // nested-sprint-* (useNestedSprintActions) and template-* (offline
        // fallback in useTemplateActions) are the only ids minted client-side.
        const isClientMintedId =
          typeof sprintToSave.id === 'string' &&
          (sprintToSave.id.startsWith('nested-sprint-') ||
            sprintToSave.id.startsWith('template-'));
        if (notFound && isClientMintedId) {
          await SprintAPI.create(sprintToSave, { workspaceId });
        } else {
          throw updateError;
        }
      }

      if (
        saveGenRef.current[sprintToSave.id] === gen &&
        !latestPayloadRef.current[sprintToSave.id]
      ) {
        clearPendingTaskAdds(workspaceId, sprintToSave.id, sprintToSave.tasks);
      }
      
      // Update last saved count AND max seen count on successful save.
      // Resetting maxSeenCount to the current count allows further intentional
      // deletions without being blocked by a stale high-water mark.
      lastSavedTaskCountRef.current[sprintToSave.id] = taskCount;
      maxTaskCountRef.current[sprintToSave.id] = taskCount;

      if (allowEmptySave) {
        clearIntentionalEmptyTasksSave(sprintToSave.id);
        logAuditEvent('INTENTIONAL_EMPTY_CAMPAIGN_SAVED', {
          sprintId: sprintToSave.id,
          sprintName: sprintToSave.name,
        });
      }
      
      // Record save time for throttling
      lastSaveTimeRef.current[sprintToSave.id] = Date.now();
      
      setSaveStatus('saved');
      // Reset to idle after 2 seconds
      if (saveStatusTimeoutRef.current) clearTimeout(saveStatusTimeoutRef.current);
      saveStatusTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Failed to save sprint:', error);
      logAuditEvent('ERROR_SAVE_FAILED', {
        sprintId: sprint.id,
        sprintName: sprint.name,
        error: error.message,
      });
      if (isDynamoItemSizeError(error)) {
        showToast(
          `"${sprint.name || 'This campaign'}" is too large for DynamoDB (400 KB cap). The last save did not land.`,
          { type: 'error', duration: 12000 },
        );
      }
      setSaveStatus('idle');
    }
  }, []);

  const scheduleSave = useCallback((sprint, workspaceId) => {
    if (!sprint) return Promise.resolve();
    if (isSprintSaveHeld(sprint.id)) return Promise.resolve();
    if (Array.isArray(sprint.tasks)) {
      const lastCount = lastSavedTaskCountRef.current[sprint.id] || 0;
      if (sprint.tasks.length > lastCount) {
        queuePendingTaskAdds(workspaceId, sprint.id, sprint.tasks);
      }
    }
    latestPayloadRef.current[sprint.id] = { sprint, workspaceId };
    const prev = saveChainRef.current[sprint.id] || Promise.resolve();
    const next = prev
      .catch(() => {})
      .then(async () => {
        const latest = latestPayloadRef.current[sprint.id];
        if (!latest) return;
        latestPayloadRef.current[sprint.id] = null;
        await performSave(latest.sprint, latest.workspaceId);
      });
    saveChainRef.current[sprint.id] = next;
    return next;
  }, [performSave]);

  // Throttled save function - ensures minimum interval between saves
  const saveSprint = useCallback(async (sprint) => {
    if (!sprint) return;
    if (isSprintSaveHeld(sprint.id)) return;
    
    // Bind the save to the workspace active RIGHT NOW; if the timer fires (or
    // the request resolves) after a workspace switch, it still lands here.
    const workspaceId = pinWorkspace();
    
    const now = Date.now();
    const lastSaveTime = lastSaveTimeRef.current[sprint.id] || 0;
    const timeSinceLastSave = now - lastSaveTime;
    
    // If enough time has passed, save immediately
    if (timeSinceLastSave >= MIN_SAVE_INTERVAL_MS) {
      // Clear any pending save since we're saving now
      if (pendingSaveTimerRef.current[sprint.id]) {
        clearTimeout(pendingSaveTimerRef.current[sprint.id]);
        pendingSaveTimerRef.current[sprint.id] = null;
      }
      pendingSaveRef.current[sprint.id] = null;
      
      await scheduleSave(sprint, workspaceId);
    } else {
      // Too soon - queue this save for later
      // Always update pending data to latest (we want to save the most recent state)
      pendingSaveRef.current[sprint.id] = { sprint, workspaceId };
      
      // Only set timer if one isn't already running
      if (!pendingSaveTimerRef.current[sprint.id]) {
        const delay = MIN_SAVE_INTERVAL_MS - timeSinceLastSave;
        pendingSaveTimerRef.current[sprint.id] = setTimeout(async () => {
          const pending = pendingSaveRef.current[sprint.id];
          pendingSaveTimerRef.current[sprint.id] = null;
          pendingSaveRef.current[sprint.id] = null;
          
          if (pending) {
            await scheduleSave(pending.sprint, pending.workspaceId);
          }
        }, delay);
      }
    }
  }, [scheduleSave, pinWorkspace]);

  /** Bypass the 2s throttle — used for critical metadata (e.g. variables). */
  const saveSprintImmediate = useCallback(async (sprint) => {
    if (!sprint) return;
    if (isSprintSaveHeld(sprint.id)) return;
    const workspaceId = pinWorkspace();
    if (pendingSaveTimerRef.current[sprint.id]) {
      clearTimeout(pendingSaveTimerRef.current[sprint.id]);
      pendingSaveTimerRef.current[sprint.id] = null;
    }
    // The caller's payload supersedes any queued body for this sprint
    pendingSaveRef.current[sprint.id] = null;
    await scheduleSave(sprint, workspaceId);
  }, [scheduleSave, pinWorkspace]);

  /** Best-effort: push any throttled pending bodies before the tab dies (F5). */
  const flushPendingSavesKeepalive = useCallback(() => {
    const pendingIds = Object.keys(pendingSaveRef.current);
    if (pendingIds.length === 0) return;

    let token = null;
    try {
      token = window.Auth?.getIdToken?.() || null;
      if (typeof token === 'string') {
        token = token.trim().replace(/^bearer\s+/i, '');
      }
    } catch {
      token = null;
    }
    if (!token) return;

    const apiUrl = APP_CONFIG.apiUrl;
    if (!apiUrl) return;

    for (const id of pendingIds) {
      const pending = pendingSaveRef.current[id];
      const sprint = pending?.sprint;
      if (!sprint || !Array.isArray(sprint.tasks)) continue;
      if (isSprintSaveHeld(id)) continue;
      if (pendingSaveTimerRef.current[id]) {
        clearTimeout(pendingSaveTimerRef.current[id]);
        pendingSaveTimerRef.current[id] = null;
      }
      pendingSaveRef.current[id] = null;

      // Each pending save carries the workspace it originated in
      const wsId = pending.workspaceId;
      const workspaceHeaders =
        wsId && wsId !== PERSONAL_WORKSPACE_ID ? { 'X-Workspace-Id': wsId } : {};

      const body = JSON.stringify({ ...sprint, categories: sprint.categories || [] });
      try {
        fetch(`${apiUrl}/sprints/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            ...workspaceHeaders,
          },
          body,
          keepalive: true,
        }).catch(() => {});
      } catch {
        // page is unloading — ignore
      }
    }
  }, []);

  // Flush throttled saves on hard refresh / tab close
  useEffect(() => {
    const onPageHide = () => {
      Object.values(abortRef.current).forEach((ac) => ac?.abort());
      flushPendingSavesKeepalive();
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onPageHide);
    };
  }, [flushPendingSavesKeepalive]);

  /**
   * Workspace switch: push every queued body to the workspace it came from
   * (saves are workspace-pinned, so this is safe after the global has already
   * switched), then reset per-sprint tracking so counts from one workspace
   * can't block or corrupt saves in another.
   */
  const flushAllPendingSaves = useCallback(() => {
    for (const id of Object.keys(pendingSaveRef.current)) {
      const pending = pendingSaveRef.current[id];
      if (pendingSaveTimerRef.current[id]) {
        clearTimeout(pendingSaveTimerRef.current[id]);
        pendingSaveTimerRef.current[id] = null;
      }
      pendingSaveRef.current[id] = null;
      if (pending?.sprint && !isSprintSaveHeld(id)) {
        // Fire-and-forget: the UI has moved to another workspace
        scheduleSave(pending.sprint, pending.workspaceId).catch(() => {});
      }
    }
    pendingSaveRef.current = {};
    lastSaveTimeRef.current = {};
    lastSavedTaskCountRef.current = {};
    maxTaskCountRef.current = {};
  }, [scheduleSave]);

  const cancelPendingSaveForSprint = useCallback((sprintId) => {
    if (!sprintId) return;
    if (pendingSaveTimerRef.current[sprintId]) {
      clearTimeout(pendingSaveTimerRef.current[sprintId]);
      pendingSaveTimerRef.current[sprintId] = null;
    }
    delete pendingSaveRef.current[sprintId];
    delete latestPayloadRef.current[sprintId];
    abortRef.current[sprintId]?.abort();
    saveGenRef.current[sprintId] = (saveGenRef.current[sprintId] || 0) + 1;
  }, []);

  return {
    saveStatus,
    saveSprint,
    saveSprintImmediate,
    flushAllPendingSaves,
    cancelPendingSaveForSprint,
  };
}

